import { inject } from '@angular/core';
import { CanMatchFn, Router, UrlSegment } from '@angular/router';
import { APP } from '@constants/app.constants';
import { SURE_PASS_STORAGE_KEYS, TOASTER_TYPE } from '@constants/app.enums';
import { SurePassCompleteFlowResponse } from '@models/profile.model';
import { AuthService } from '@services/auth.service';
import { ProfileService } from '@services/profile.service';
import { ToasterService } from '@services/toaster.service';
import { UserStateService } from '@services/user-state.service';

// Flag to prevent multiple SurePass flow executions
let isProcessingSurePass = false;

export const LoginGuard: CanMatchFn = (_, segments: UrlSegment[]) => {
  const userStateService = inject(UserStateService);
  const router = inject(Router);
  const profileService = inject(ProfileService);
  const toasterService = inject(ToasterService);
  const authService = inject(AuthService);

  const token = userStateService.getToken();
  const clientId = localStorage.getItem(SURE_PASS_STORAGE_KEYS.CLIENT_ID);

  // Handle SurePass flow if clientId exists and not already processing
  if (clientId && !isProcessingSurePass) {
    isProcessingSurePass = true;
    handleSurePassFlow(
      clientId,
      profileService,
      toasterService,
      authService,
      router
    );
    return false; // Prevent navigation while processing SurePass
  }

  // If already processing SurePass, allow navigation to prevent loop
  if (isProcessingSurePass) {
    return true;
  }

  if (!token || checkRoute(segments, APP.LOGOUT)) {
    return true;
  }

  router.navigate(['/admin']);
  return false;
};

const handleSurePassFlow = (
  clientId: string,
  profileService: ProfileService,
  toasterService: ToasterService,
  authService: AuthService,
  router: Router
): void => {
  profileService.completeSurePassFlow(clientId).subscribe({
    next: (response: SurePassCompleteFlowResponse) => {
      handleSurePassCompletion(
        response,
        clientId,
        toasterService,
        authService,
        router
      );
    },
    error: () => {
      handleSurePassError(clientId, toasterService, authService, router);
    }
  });
};

const handleSurePassCompletion = (
  response: SurePassCompleteFlowResponse,
  clientId: string,
  toasterService: ToasterService,
  authService: AuthService,
  router: Router
): void => {
  localStorage.removeItem(SURE_PASS_STORAGE_KEYS.CLIENT_ID);
  isProcessingSurePass = false; // Reset flag

  if (response.status === 'success') {
    toasterService.display('Logged in successfully', TOASTER_TYPE.SUCCESS);
    router.navigate(['/admin/dashboard']);
  } else {
    toasterService.display(
      'DigiLocker verification failed',
      TOASTER_TYPE.ERROR
    );
    authService.logout().subscribe({
      next: () => {
        router.navigate(['/auth/login']);
      },
      error: () => {
        router.navigate(['/auth/login']);
      }
    });
  }
};

const handleSurePassError = (
  clientId: string,
  toasterService: ToasterService,
  authService: AuthService,
  router: Router
): void => {
  localStorage.removeItem(SURE_PASS_STORAGE_KEYS.CLIENT_ID);
  isProcessingSurePass = false; // Reset flag
  toasterService.display('DigiLocker verification failed. Please try again.');

  authService.logout().subscribe({
    next: () => {
      router.navigate(['/auth/login']);
    },
    error: () => {
      router.navigate(['/auth/login']);
    }
  });
};

const checkRoute = (segments: UrlSegment[], route: string) => {
  return segments.some((segment) => segment.path.includes(route));
};
